import React, { useEffect, useRef } from 'react';
import useStore from '../store';
import { EVENTS_DATA } from '../data/events';
import '../styles/cinematic.css';
import gsap from 'gsap';

const DetailView = () => {
    const stage = useStore(state => state.stage);
    const zoomedCategoryId = useStore(state => state.zoomedCategoryId);
    const setZoomedCategoryId = useStore(state => state.setZoomedCategoryId);
    const setWheelLocked = useStore(state => state.setWheelLocked);

    const containerRef = useRef();

    useEffect(() => {
        if (zoomedCategoryId) {
            gsap.fromTo(containerRef.current,
                { opacity: 0, y: 50 },
                { opacity: 1, y: 0, duration: 0.5, delay: 0.5, ease: "power2.out" }
            );
        }
    }, [zoomedCategoryId]);

    if (!zoomedCategoryId) return null;

    const data = EVENTS_DATA[zoomedCategoryId] || { title: 'Unknown', events: [] };

    const handleBack = () => {
        gsap.to(containerRef.current, {
            opacity: 0,
            y: 50,
            duration: 0.3,
            onComplete: () => {
                setZoomedCategoryId(null);
                setWheelLocked(false);
            }
        });
    };

    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(5px)',
            zIndex: 2001,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'auto'
        }}>
            <div ref={containerRef} style={{
                width: window.innerWidth < 768 ? '95%' : '90%',
                maxWidth: '1000px',
                height: window.innerWidth < 768 ? '90vh' : '80vh',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '4px',
                background: 'rgba(10, 10, 15, 0.95)',
                boxShadow: '0 0 40px rgba(0, 255, 255, 0.2), inset 0 0 20px rgba(0,0,0,0.8)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                position: 'relative'
            }}>
                {/* Mobile Close Button (Top Right) */}
                <div
                    onClick={handleBack}
                    style={{
                        position: 'absolute',
                        top: '20px',
                        right: '25px',
                        color: '#fff',
                        fontSize: '2rem', // Increased size
                        cursor: 'pointer',
                        zIndex: 10,
                        display: window.innerWidth < 768 ? 'block' : 'none',
                        padding: '15px', // Added padding for touch area
                        background: 'rgba(0,0,0,0.3)', // Optional: background for visibility
                        borderRadius: '50%'
                    }}
                >
                    ✕
                </div>

                {/* Header Line */}
                <div style={{
                    height: '4px',
                    width: '100%',
                    background: 'linear-gradient(90deg, #ff007f, #00ffff)',
                    boxShadow: '0 0 10px #00ffff'
                }} />

                {/* Content Container */}
                <div style={{ padding: window.innerWidth < 768 ? '20px' : '40px', overflowY: 'auto', flex: 1 }} className="no-scrollbar">
                    <h1 className="text-glitch" data-text={data.title} style={{
                        fontSize: window.innerWidth < 768 ? '2.5rem' : '4rem',
                        fontFamily: 'var(--font-header)',
                        color: '#fff',
                        textTransform: 'uppercase',
                        marginBottom: '10px'
                    }}>
                        {data.title}
                    </h1>
                    <p style={{
                        color: '#aaa',
                        fontSize: '1.2rem',
                        marginBottom: '40px',
                        borderLeft: '2px solid #ff007f',
                        paddingLeft: '20px'
                    }}>
                        {data.description}
                    </p>

                    {/* Events Grid */}
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                        gap: '20px'
                    }}>
                        {data.events.map((ev, i) => (
                            <div key={i} className="event-card" style={{
                                background: 'rgba(255,255,255,0.03)',
                                border: '1px solid rgba(255,255,255,0.1)',
                                padding: '25px',
                                transition: 'all 0.3s ease'
                            }}>
                                <span style={{
                                    display: 'inline-block',
                                    padding: '4px 8px',
                                    background: 'rgba(0, 255, 255, 0.1)',
                                    color: '#00ffff',
                                    fontSize: '0.8rem',
                                    marginBottom: '10px',
                                    border: '1px solid rgba(0, 255, 255, 0.3)'
                                }}>{ev.type}</span>
                                <h3 style={{
                                    margin: '0 0 10px 0',
                                    fontSize: '1.8rem',
                                    color: '#fff'
                                }}>{ev.title}</h3>
                                <p style={{ color: '#888', fontSize: '0.9rem', marginBottom: '20px' }}>
                                    {ev.desc}
                                </p>
                                {ev.link && ev.link !== '#' && (
                                    <button style={{
                                        width: '100%',
                                        padding: '12px',
                                        background: 'transparent',
                                        border: '1px solid #ff007f',
                                        color: '#ff007f',
                                        textTransform: 'uppercase',
                                        letterSpacing: '1px',
                                        cursor: 'pointer',
                                        transition: '0.3s'
                                    }}
                                        onClick={() => {
                                            window.open(ev.link, '_blank');
                                        }}
                                        onMouseOver={(e) => {
                                            e.target.style.background = '#ff007f';
                                            e.target.style.color = '#fff';
                                            e.target.style.boxShadow = '0 0 15px #ff007f';
                                        }}
                                        onMouseOut={(e) => {
                                            e.target.style.background = 'transparent';
                                            e.target.style.color = '#ff007f';
                                            e.target.style.boxShadow = 'none';
                                        }}
                                    >
                                        Register
                                    </button>
                                )}
                            </div>
                        ))}
                    </div>
                </div>

                {/* Footer / Back */}
                <div style={{ padding: '20px 40px', borderTop: '1px solid rgba(255,255,255,0.1)', textAlign: 'right' }}>
                    <button
                        onClick={handleBack}
                        style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#fff',
                            fontSize: '1.2rem',
                            cursor: 'pointer',
                            textTransform: 'uppercase',
                            letterSpacing: '2px',
                            display: 'flex',
                            alignItems: 'center',
                            marginLeft: 'auto',
                            gap: '10px',
                            padding: '15px' // Added padding for hit area
                        }}
                    >
                        <span>✕</span> Close Interface
                    </button>
                </div>
            </div>
        </div>
    );
};

export default DetailView;
